import { useState, useEffect, useRef, useCallback } from 'react';
import toast from 'react-hot-toast';
import api from '../services/api';
import { Zap, Bot, CreditCard, ArrowRight, X, Lock, QrCode, Copy, Check, Smartphone, Settings, Calendar } from 'lucide-react';
import { useT } from '../i18n';
import { useAuth } from '../context/AuthContext';

function loadStripeScript(key) {
  return new Promise((resolve, reject) => {
    if (window.Stripe) { resolve(window.Stripe(key)); return; }
    const script = document.createElement('script');
    script.src = 'https://js.stripe.com/v3/';
    script.onload = () => resolve(window.Stripe(key));
    script.onerror = reject;
    document.head.appendChild(script);
  });
}

const PLANO_LABEL = {
  freemium:   'Freemium',
  pendente:   'Pendente',
  starter:    'Starter',
  essencial:  'Essencial',
  pro:        'Profissional',
  enterprise: 'Empresarial',
  performance1: 'Performance 1',
  performance2: 'Performance 2',
};

export default function Creditos() {
  const { t } = useT();
  const { quotaIa } = useAuth();
  const [assinatura, setAssinatura] = useState(null);
  const [planoAtual, setPlanoAtual] = useState(null);
  const [abrindoPortal, setAbrindoPortal] = useState(false);
  const [renovando, setRenovando] = useState(false);
  const [carregando, setCarregando] = useState(true);
  // null | pack object = mostra modal de escolha de método
  const [modalPack, setModalPack] = useState(null);
  // 'escolha' | 'cartao' | 'pix'
  const [etapaModal, setEtapaModal] = useState('escolha');
  const [processando, setProcessando] = useState(false);
  // PIX state
  const [pixData, setPixData] = useState(null); // { brCode, qrCodeImage, correlationID, label, valor }
  const [pixCopiado, setPixCopiado] = useState(false);
  const [pixPago, setPixPago] = useState(false);
  const pollRef = useRef(null);
  const cardElRef = useRef(null);
  const stripeRef = useRef(null);
  const clientSecretRef = useRef(null);

  const PACKS = [
    {
      id: 'pack_disparos',
      icon: <Zap size={28} color="#16a34a" />,
      nome: t('creditos.pack_disparos_nome'),
      descricao: t('creditos.pack_disparos_descricao'),
      preco: 39,
      cor: '#16a34a',
      destaque: false,
      itens: [
        t('creditos.pack_disparos_item1'),
        t('creditos.pack_disparos_item2'),
        t('creditos.pack_disparos_item3'),
        t('creditos.pack_disparos_item4'),
      ],
    },
    {
      id: 'pack_ia',
      icon: <Bot size={28} color="#13305f" />,
      nome: t('creditos.pack_ia_nome'),
      descricao: t('creditos.pack_ia_descricao'),
      preco: 187,
      cor: '#13305f',
      destaque: true,
      itens: [
        t('creditos.pack_ia_item1'),
        t('creditos.pack_ia_item2'),
        t('creditos.pack_ia_item3'),
        t('creditos.pack_ia_item4'),
      ],
    },
  ];

  useEffect(() => {
    Promise.all([
      api.get('/pagamento/minha-assinatura').then(r => r.data).catch(() => null),
      api.get('/pagamento/plano-atual').then(r => r.data).catch(() => null),
    ]).then(([ass, plano]) => {
      setAssinatura(ass);
      setPlanoAtual(plano);
    }).finally(() => setCarregando(false));
  }, []);

  // Limpa polling ao desmontar para evitar memory leak
  useEffect(() => {
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  async function abrirPortalStripe() {
    setAbrindoPortal(true);
    try {
      const { data } = await api.post('/pagamento/portal-stripe');
      window.location.href = data.url;
    } catch (err) {
      toast.error(err.response?.data?.erro || 'Não foi possível abrir o portal de assinatura');
      setAbrindoPortal(false);
    }
  }

  async function renovarPlano() {
    setRenovando(true);
    try {
      const { data } = await api.post('/pagamento/renovar');
      window.location.href = data.url;
    } catch (err) {
      toast.error(err.response?.data?.erro || 'Não foi possível gerar o link de renovação');
      setRenovando(false);
    }
  }

  const ehFreemium = !assinatura || assinatura.plano === 'freemium' || assinatura.plano === 'pendente' || !assinatura.plano;

  function abrirModal(pack) {
    setModalPack(pack);
    setEtapaModal('escolha');
    setProcessando(false);
    setPixData(null);
    setPixCopiado(false);
    setPixPago(false);
  }

  function fecharModal() {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    setModalPack(null);
    setEtapaModal('escolha');
    setPixData(null);
    clientSecretRef.current = null;
    stripeRef.current = null;
  }

  async function escolherCartao() {
    setEtapaModal('cartao');
    setProcessando(true);
    try {
      const { data } = await api.post('/pagamento/criar-credito-intent', { pack: modalPack.id });
      clientSecretRef.current = data.client_secret;
      const stripe = await loadStripeScript(data.publishable_key);
      stripeRef.current = stripe;
      setTimeout(() => {
        if (!cardElRef.current) return;
        const elements = stripe.elements();
        const card = elements.create('card', {
          style: {
            base: {
              fontSize: '16px', color: '#13305f',
              fontFamily: '-apple-system, BlinkMacSystemFont, Segoe UI, sans-serif',
              '::placeholder': { color: '#aaa' },
            },
          },
        });
        card.mount(cardElRef.current);
        cardElRef.current._stripeCard = card;
      }, 100);
    } catch (err) {
      toast.error(err.response?.data?.erro || t('creditos.toast_erro_iniciar'));
      setEtapaModal('escolha');
    } finally {
      setProcessando(false);
    }
  }

  async function escolherPix() {
    setEtapaModal('pix');
    setProcessando(true);
    try {
      const { data } = await api.post('/pagamento/openpix/criar-credito', { pack: modalPack.id });
      setPixData(data);
      iniciarPolling(data.correlationID);
    } catch (err) {
      toast.error(err.response?.data?.erro || t('creditos.toast_erro_iniciar'));
      setEtapaModal('escolha');
    } finally {
      setProcessando(false);
    }
  }

  const iniciarPolling = useCallback((correlationID) => {
    if (pollRef.current) clearInterval(pollRef.current);
    let tentativas = 0;
    pollRef.current = setInterval(async () => {
      tentativas++;
      if (tentativas > 60) { clearInterval(pollRef.current); return; } // 5 min
      try {
        const { data } = await api.get(`/pagamento/openpix/status/${correlationID}`);
        if (data.status === 'pago') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setPixPago(true);
          toast.success(t('creditos.toast_pix_pago'));
          setTimeout(() => fecharModal(), 2500);
        }
      } catch { /* ignora erros de polling */ }
    }, 5000);
  }, []); // eslint-disable-line

  function copiarBrCode() {
    if (!pixData?.brCode) return;
    navigator.clipboard.writeText(pixData.brCode).then(() => {
      setPixCopiado(true);
      setTimeout(() => setPixCopiado(false), 2000);
    });
  }

  async function confirmarPagamento() {
    if (!stripeRef.current || !clientSecretRef.current || !cardElRef.current?._stripeCard) return;
    setProcessando(true);
    try {
      const { error, paymentIntent } = await stripeRef.current.confirmCardPayment(clientSecretRef.current, {
        payment_method: { card: cardElRef.current._stripeCard },
      });
      if (error) {
        toast.error(error.message);
        setProcessando(false);
        return;
      }
      if (paymentIntent.status === 'succeeded') {
        toast.success(t('creditos.toast_pack_adquirido', { nome: modalPack.nome }));
        fecharModal();
      }
    } catch (err) {
      toast.error(err.message);
      setProcessando(false);
    }
  }

  if (carregando) {
    return (
      <div style={{ padding: '3rem', textAlign: 'center', color: '#9aa0b6' }}>
        {t('creditos.carregando')}
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: 'clamp(1.5rem, 4vw, 2.5rem) clamp(1rem, 3vw, 2rem)' }}>
      <h1 style={{ fontSize: 'clamp(1.3rem, 3vw, 1.8rem)', fontWeight: 700, margin: '0 0 0.25rem' }}>
        {t('creditos.titulo')}
      </h1>
      <p style={{ color: '#9aa0b6', fontSize: 14, margin: '0 0 2rem' }}>
        {t('creditos.subtitulo')}
      </p>

      {/* Meu Plano */}
      {planoAtual && <MeuPlanoCard plano={planoAtual} onPortal={abrirPortalStripe} abrindoPortal={abrindoPortal} onRenovar={renovarPlano} renovando={renovando} />}

      {/* Alerta: sem acesso à Concierge IA */}
      {quotaIa && !quotaIa.ilimitado && quotaIa.limite === 0 && !quotaIa.creditosIA && (
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: 14,
          background: 'rgba(139,92,246,0.08)', border: '1px solid rgba(139,92,246,0.35)',
          borderRadius: 12, padding: '1rem 1.25rem', marginBottom: '1.5rem',
        }}>
          <Bot size={20} color="#13305f" style={{ flexShrink: 0, marginTop: 2 }} />
          <div>
            <p style={{ margin: '0 0 4px', fontWeight: 600, color: '#13305f', fontSize: 14 }}>
              Concierge IA não disponível
            </p>
            <p style={{ margin: 0, color: '#b9bdd1', fontSize: 13, lineHeight: 1.6 }}>
              Seu plano atual não inclui mensagens de Concierge IA. Adquira o Pack IA abaixo ou faça upgrade para um plano que inclua IA.
            </p>
          </div>
        </div>
      )}

      {/* Alerta freemium */}
      {ehFreemium && (
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: 14,
          background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.35)',
          borderRadius: 12, padding: '1rem 1.25rem', marginBottom: '2rem',
        }}>
          <Lock size={20} color="#f59e0b" style={{ flexShrink: 0, marginTop: 2 }} />
          <div>
            <p style={{ margin: '0 0 6px', fontWeight: 600, color: '#f59e0b', fontSize: 14 }}>
              {t('creditos.freemium_titulo')}
            </p>
            <p style={{ margin: 0, color: '#b9bdd1', fontSize: 13, lineHeight: 1.6 }}>
              {t('creditos.freemium_texto')}
            </p>
            <a
              href="https://disparaia.net/#precos"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 10,
                padding: '8px 16px', borderRadius: 8,
                background: 'linear-gradient(135deg, #16a34a, #13305f)',
                color: '#fff', fontSize: 13, fontWeight: 600,
              }}
            >
              {t('creditos.ver_planos')} <ArrowRight size={14} />
            </a>
          </div>
        </div>
      )}

      {/* Packs */}
      <div id="packs" style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
        gap: '1rem',
        alignItems: 'stretch',
        opacity: ehFreemium ? 0.5 : 1,
        pointerEvents: ehFreemium ? 'none' : 'auto',
      }}>
        {PACKS.map(pack => (
          <div key={pack.id} style={{
            background: 'var(--color-surface, #1c1f2a)',
            border: pack.destaque ? `1.5px solid ${pack.cor}` : '1px solid rgba(255,255,255,0.06)',
            borderRadius: 14,
            padding: '1.1rem 1.25rem',
            position: 'relative',
            boxShadow: pack.destaque ? `0 8px 32px ${pack.cor}22` : 'none',
            display: 'flex',
            flexDirection: 'column',
          }}>
            {pack.economia && (
              <span style={{
                position: 'absolute', top: -11, left: 16,
                background: '#16a34a', color: '#fff',
                fontSize: 11, fontWeight: 700,
                padding: '3px 10px', borderRadius: 999,
              }}>
                {pack.economia}
              </span>
            )}

            {/* Cabeçalho: ícone + nome + preço em linha */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
              <div style={{
                width: 42, height: 42, borderRadius: 11, flexShrink: 0,
                background: `${pack.cor}18`, border: `1px solid ${pack.cor}44`,
                display: 'grid', placeItems: 'center',
              }}>
                {pack.icon}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 15, fontWeight: 700 }}>{pack.nome}</div>
                <div style={{ fontSize: 12, color: '#9aa0b6' }}>{pack.descricao}</div>
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 2 }}>
                  <span style={{ fontSize: 11, color: '#9aa0b6' }}>R$</span>
                  <span style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-0.02em' }}>{pack.preco}</span>
                </div>
                <div style={{ fontSize: 11, color: '#9aa0b6' }}>{t('creditos.por_pack')}</div>
              </div>
            </div>

            {/* Lista compacta */}
            <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 1rem', flex: 1 }}>
              {pack.itens.map(item => (
                <li key={item} style={{ display: 'flex', gap: 7, fontSize: 12, color: '#b9bdd1', padding: '3px 0' }}>
                  <span style={{ color: '#22c55e', fontWeight: 700, flexShrink: 0 }}>✓</span>
                  {item}
                </li>
              ))}
            </ul>

            <button
              onClick={() => abrirModal(pack)}
              style={{
                width: '100%', padding: '10px', borderRadius: 10,
                background: `linear-gradient(135deg, ${pack.cor}, ${pack.cor}cc)`,
                border: 'none',
                color: '#fff', fontWeight: 600, fontSize: 14, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                opacity: pack.destaque ? 1 : 0.85,
              }}
            >
              <CreditCard size={15} /> {t('creditos.btn_comprar', { preco: pack.preco })}
            </button>
          </div>
        ))}
      </div>

      <p style={{ fontSize: 12, color: '#6b7280', textAlign: 'center', marginTop: '1.5rem' }}>
        {t('creditos.rodape')}
      </p>

      {/* Modal de pagamento */}
      {modalPack && (
        <div style={{
          position: 'fixed', inset: 0,
          background: 'rgba(0,0,0,0.6)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem',
        }} onClick={e => { if (e.target === e.currentTarget) fecharModal(); }}>
          <div style={{
            background: 'var(--color-surface, #1c1f2a)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 16, padding: '1.75rem',
            width: '100%', maxWidth: 440,
            boxShadow: '0 24px 64px rgba(0,0,0,0.5)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
              <h3 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>{t('creditos.modal_titulo')}</h3>
              <button onClick={fecharModal} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9aa0b6', padding: 4 }}>
                <X size={20} />
              </button>
            </div>

            {/* Resumo do pack */}
            <div style={{
              background: `${modalPack.cor}22`,
              border: `1.5px solid ${modalPack.cor}`,
              borderRadius: 10, padding: '12px 16px', marginBottom: '1.25rem',
              fontSize: 14,
            }}>
              <strong>{modalPack.nome}</strong> — {modalPack.descricao}
              <div style={{ fontSize: 20, fontWeight: 700, marginTop: 4, color: modalPack.cor }}>R$ {modalPack.preco},00</div>
            </div>

            {/* Etapa: escolha do método */}
            {etapaModal === 'escolha' && (
              <div>
                <p style={{ fontSize: 13, color: '#9aa0b6', margin: '0 0 1rem' }}>{t('creditos.escolha_metodo')}</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <button
                    onClick={escolherPix}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 12,
                      padding: '14px 18px', borderRadius: 12,
                      background: 'rgba(34,197,94,0.1)', border: '1.5px solid rgba(34,197,94,0.4)',
                      color: '#e6e6f0', cursor: 'pointer', fontSize: 15, fontWeight: 600,
                    }}
                  >
                    <QrCode size={20} color="#22c55e" />
                    <div style={{ textAlign: 'left' }}>
                      <div>{t('creditos.pix_titulo')}</div>
                      <div style={{ fontSize: 12, color: '#9aa0b6', fontWeight: 400 }}>{t('creditos.pix_sub')}</div>
                    </div>
                  </button>
                  <button
                    onClick={escolherCartao}
                    disabled={processando}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 12,
                      padding: '14px 18px', borderRadius: 12,
                      background: 'rgba(22, 163, 74,0.1)', border: '1.5px solid rgba(22, 163, 74,0.4)',
                      color: '#e6e6f0', cursor: processando ? 'not-allowed' : 'pointer',
                      fontSize: 15, fontWeight: 600, opacity: processando ? 0.6 : 1,
                    }}
                  >
                    <CreditCard size={20} color="#4ade80" />
                    <div style={{ textAlign: 'left' }}>
                      <div>{t('creditos.cartao_titulo')}</div>
                      <div style={{ fontSize: 12, color: '#9aa0b6', fontWeight: 400 }}>{t('creditos.cartao_sub')}</div>
                    </div>
                  </button>
                </div>
              </div>
            )}

            {/* Etapa: cartão */}
            {etapaModal === 'cartao' && (
              <div>
                <button
                  onClick={() => setEtapaModal('escolha')}
                  style={{ background: 'none', border: 'none', color: '#9aa0b6', cursor: 'pointer', fontSize: 13, padding: '0 0 10px', display: 'flex', alignItems: 'center', gap: 4 }}
                >
                  ← {t('creditos.voltar')}
                </button>
                <label style={{ fontSize: 12, color: '#9aa0b6', display: 'block', marginBottom: 8 }}>
                  {t('creditos.label_cartao')}
                </label>
                <div
                  ref={cardElRef}
                  style={{ background: '#fff', borderRadius: 8, padding: '12px 14px', marginBottom: '1.25rem', border: '1px solid #e6e8ef' }}
                />
                <button
                  onClick={confirmarPagamento}
                  disabled={processando}
                  style={{
                    width: '100%', padding: '13px', borderRadius: 10,
                    background: processando ? '#9aa0b6' : `linear-gradient(135deg, ${modalPack.cor}, ${modalPack.cor}cc)`,
                    border: 'none', color: '#fff', fontWeight: 600, fontSize: 15,
                    cursor: processando ? 'not-allowed' : 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  }}
                >
                  {processando ? t('creditos.btn_processando') : <><CreditCard size={16} /> {t('creditos.btn_pagar', { preco: modalPack.preco })}</>}
                </button>
                <p style={{ fontSize: 12, color: '#6b7280', textAlign: 'center', marginTop: '0.75rem' }}>
                  {t('creditos.modal_rodape')}
                </p>
              </div>
            )}

            {/* Etapa: PIX */}
            {etapaModal === 'pix' && (
              <div>
                {processando && (
                  <div style={{ textAlign: 'center', color: '#9aa0b6', padding: '2rem 0' }}>{t('creditos.pix_gerando')}</div>
                )}
                {!processando && pixData && !pixPago && (
                  <>
                    <p style={{ fontSize: 13, color: '#9aa0b6', margin: '0 0 1rem' }}>{t('creditos.pix_instrucao')}</p>
                    {pixData.qrCodeImage && (
                      <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
                        <img src={pixData.qrCodeImage} alt="QR Code PIX" style={{ width: 180, height: 180, borderRadius: 8 }} />
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: 8, alignItems: 'stretch', marginBottom: '1rem' }}>
                      <input
                        readOnly value={pixData.brCode}
                        style={{ flex: 1, padding: '10px 12px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.05)', color: '#9aa0b6', fontSize: 11, fontFamily: 'monospace' }}
                      />
                      <button
                        onClick={copiarBrCode}
                        style={{
                          padding: '10px 14px', borderRadius: 8, border: 'none',
                          background: pixCopiado ? '#16a34a' : 'rgba(22, 163, 74,0.3)',
                          color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, fontWeight: 600, flexShrink: 0,
                        }}
                      >
                        {pixCopiado ? <><Check size={14} /> {t('creditos.copiado')}</> : <><Copy size={14} /> {t('creditos.copiar')}</>}
                      </button>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.25)', borderRadius: 8, fontSize: 12, color: '#fbbf24' }}>
                      <Smartphone size={14} />
                      {t('creditos.pix_aguardando')}
                    </div>
                  </>
                )}
                {pixPago && (
                  <div style={{ textAlign: 'center', padding: '1.5rem 0' }}>
                    <div style={{ fontSize: 40, marginBottom: 8 }}>✅</div>
                    <p style={{ fontWeight: 600, color: '#22c55e', margin: 0 }}>{t('creditos.pix_confirmado')}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Barra({ usado, limite, creditos = 0, cor }) {
  const limiteEfetivo = limite + creditos;
  const pct = limiteEfetivo > 0 ? Math.min(100, Math.round((usado / limiteEfetivo) * 100)) : 0;
  const corBarra = pct >= 90 ? '#ef4444' : pct >= 70 ? '#f59e0b' : cor;
  return (
    <div style={{ marginTop: 6 }}>
      <div style={{
        height: 8, borderRadius: 999, background: 'rgba(255,255,255,0.06)',
        overflow: 'hidden',
      }}>
        <div style={{ width: `${pct}%`, height: '100%', background: corBarra, transition: 'width 240ms ease' }} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#9aa0b6', marginTop: 4 }}>
        <span>{usado.toLocaleString('pt-BR')} de {limiteEfetivo > 0 ? limiteEfetivo.toLocaleString('pt-BR') : '∞'}</span>
        {creditos > 0 && <span>+{creditos.toLocaleString('pt-BR')} avulsos</span>}
      </div>
    </div>
  );
}

const PLANOS_RENOVAVEIS = ['starter', 'essencial', 'pro', 'performance1', 'performance2'];

function MeuPlanoCard({ plano, onPortal, abrindoPortal, onRenovar, renovando }) {
  const nomePlano = PLANO_LABEL[plano.plano] || plano.plano || '—';
  const valor = plano.preco_mensal > 0 ? `R$ ${(plano.preco_mensal).toFixed(0)}` : '—';
  const venc = plano.data_vencimento ? new Date(plano.data_vencimento) : null;
  const dataVenc = venc
    ? venc.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' })
    : null;
  const ehFreemiumOuPendente = plano.plano === 'freemium' || plano.plano === 'pendente' || !plano.plano;

  const vencido = venc && venc < new Date();
  const vencendo = venc && !vencido && venc < new Date(Date.now() + 7 * 86400000);
  // Só oferece renovação self-service para planos pagos recorrentes.
  const podeRenovar = PLANOS_RENOVAVEIS.includes(plano.plano) && (vencido || vencendo);

  return (
    <div style={{
      background: 'var(--color-surface, #1c1f2a)',
      border: vencido ? '1px solid rgba(239,68,68,0.5)' : '1px solid rgba(255,255,255,0.08)',
      borderRadius: 14, padding: '1.25rem 1.5rem', marginBottom: '1.5rem',
    }}>
      {(vencido || vencendo) && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          background: vencido ? 'rgba(239,68,68,0.1)' : 'rgba(251,191,36,0.1)',
          border: `1px solid ${vencido ? 'rgba(239,68,68,0.4)' : 'rgba(251,191,36,0.35)'}`,
          borderRadius: 10, padding: '10px 14px', marginBottom: '1.25rem',
          fontSize: 13, color: vencido ? '#f87171' : '#fbbf24', fontWeight: 600,
        }}>
          <Lock size={16} style={{ flexShrink: 0 }} />
          {vencido
            ? 'Seu plano venceu. Renove para voltar a enviar campanhas e usar a Concierge IA.'
            : `Seu plano vence em ${dataVenc}. Renove para não interromper os envios.`}
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ fontSize: 12, color: '#9aa0b6', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
            Plano atual
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
            <span style={{ fontSize: 22, fontWeight: 800 }}>{nomePlano}</span>
            {plano.preco_mensal > 0 && (
              <span style={{ fontSize: 14, color: '#9aa0b6' }}>
                {valor}/{plano.periodo === 'anual' ? 'ano' : 'mês'}
              </span>
            )}
          </div>
          {dataVenc && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#9aa0b6', marginTop: 6 }}>
              <Calendar size={13} /> Próximo vencimento: <strong style={{ color: '#e6e6f0' }}>{dataVenc}</strong>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {podeRenovar && (
            <button
              onClick={onRenovar}
              disabled={renovando}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '8px 14px', borderRadius: 8, border: 'none',
                background: vencido
                  ? 'linear-gradient(135deg, #ef4444, #f97316)'
                  : 'linear-gradient(135deg, #f59e0b, #f97316)',
                color: '#fff', fontSize: 13, fontWeight: 600,
                cursor: renovando ? 'wait' : 'pointer', opacity: renovando ? 0.6 : 1,
              }}
            >
              <Zap size={14} /> {renovando ? 'Abrindo…' : 'Renovar plano'}
            </button>
          )}
          {plano.tem_assinatura_stripe && (
            <button
              onClick={onPortal}
              disabled={abrindoPortal}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '8px 14px', borderRadius: 8,
                background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)',
                color: '#e6e6f0', fontSize: 13, fontWeight: 600,
                cursor: abrindoPortal ? 'wait' : 'pointer', opacity: abrindoPortal ? 0.6 : 1,
              }}
            >
              <Settings size={14} /> {abrindoPortal ? 'Abrindo…' : 'Gerenciar assinatura'}
            </button>
          )}
          <a
            href="https://disparaia.net/#precos"
            target="_blank" rel="noopener noreferrer"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '8px 14px', borderRadius: 8,
              background: 'linear-gradient(135deg, #16a34a, #13305f)',
              color: '#fff', fontSize: 13, fontWeight: 600,
            }}
          >
            {ehFreemiumOuPendente ? 'Escolher plano' : 'Fazer upgrade'} <ArrowRight size={13} />
          </a>
        </div>
      </div>

      {/* Uso do mês */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: '1rem', marginTop: '1.25rem',
      }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600 }}>
            <Zap size={14} color="#16a34a" /> Disparos do mês
          </div>
          <Barra
            usado={plano.disparos?.usado ?? 0}
            limite={plano.disparos?.limite ?? 0}
            creditos={plano.disparos?.creditos_avulsos ?? 0}
            cor="#16a34a"
          />
        </div>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600 }}>
            <Bot size={14} color="#13305f" /> Mensagens da Concierge IA
          </div>
          <Barra
            usado={plano.ia?.usado ?? 0}
            limite={plano.ia?.limite ?? 0}
            creditos={plano.ia?.creditos_avulsos ?? 0}
            cor="#13305f"
          />
        </div>
      </div>
    </div>
  );
}
